import { useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";
import { isAuthError } from "@/lib/aws-amplify/auth";

import { DevelopmentTools } from "@/hooks/use-development-tools";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info/_hooks/use-toast";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

export const useConfirmSignUp = () => {
	const navigate = useNavigate();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { consoleErrorOnlyDevMode } = DevelopmentTools();

	const confirmSignUp = async (
		email: string,
		confirmationCode: string,
		_clientMetaData?: { [key: string]: string },
	) => {
		showLoadingScreenCallback();
		try {
			const res = await AuthWrapper.authConfirmSignUp(email, confirmationCode, _clientMetaData);

			if (res.isSignUpComplete) {
				/**
				 * @see file://./../_md/next-step/sign-up-step.md
				 */
				if (res.nextStep.signUpStep === "DONE") {
					notifyUserEventInfo({
						variant: "default",
						title: "認証に成功しました",
						description: "続けてサインインをしてください。",
					});
					navigate(PATHS.AUTH.SIGN_IN);
				} else {
					/**
					 * @description
					 * confirm sign-up のフェーズで、以下の step が送られることはあり得ない。
					 * - COMPLETE_AUTO_SIGN_IN
					 * - CONFIRM_SIGN_UP
					 */
					notifyUserEventInfo({
						variant: "error",
						title: "予期せぬエラーが発生しました",
						description: "カスタマーサポートへお問い合わせください。",
					});
				}
			}
		} catch (err) {
			if (isAuthError(err)) {
				const result = isAuthError(err);
				if (result) {
					/**
					 *
					 * @description
					 * amplify/auth から throw されるエラーは以下、
					 * 1. ConfirmSignUpException
					 * 2. AuthValidationErrorCode
					 * 3. AuthTokenConfigException
					 *
					 * 2,3 はクライアントでハンドリングしているため、1のみ考慮する。
					 */
					const errorType = err.name;
					const errorMessage = err.message;
					if (
						errorType === "TooManyFailedAttemptsException" ||
						errorType === "TooManyRequestsException" ||
						errorType === "LimitExceededException"
					) {
						notifyUserEventInfo({
							variant: "error",
							title: "認証に失敗しました",
							description: "時間を空けて、もう一度お試しください。",
						});
						navigate(PATHS.AUTH.SIGN_UP);
					} else if (errorType === "ExpiredCodeException") {
						// notifyUserEventInfo({
						// 	variant: "error",
						// 	title: "認証に失敗しました",
						// 	description: "既に認証済みです。サインインをしてください。",
						// });
						// navigate(PATHS.AUTH.SIGN_IN);
						consoleErrorOnlyDevMode(err);
					} else if (errorType === "CodeMismatchException") {
						notifyUserEventInfo({
							variant: "error",
							title: "認証に失敗しました",
							description: "認証情報が不正です。カスタマーサポートへお問い合わせください。",
						});
						navigate(PATHS.AUTH.SIGN_UP);
					} else if (
						errorType === "NotAuthorizedException" &&
						errorMessage === "User cannot be confirmed. Current status is CONFIRMED"
					) {
						notifyUserEventInfo({
							variant: "error",
							title: "認証に失敗しました",
							description: "既に認証済みです。サインインをしてください。",
						});
						navigate(PATHS.AUTH.SIGN_IN);
					} else {
						notifyUserEventInfo({
							variant: "error",
							title: "認証に失敗しました",
							description: "カスタマーサポートへお問い合わせください。",
						});
						navigate(PATHS.AUTH.SIGN_UP);
					}
				} else {
					notifyUserEventInfo({
						variant: "error",
						title: "認証に失敗しました",
						description: "もう一度お試しください。",
					});
					// navigate(PATHS.AUTH.SIGN_UP);
				}
			}
		} finally {
			hideLoadingScreenCallback();
		}
	};

	return {
		confirmSignUp,
	};
};
